import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import './AbonnementSite.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const stripePromise = loadStripe('pk_live_51RpxJH7ZtN1vRNLi08zRQxVTALUL0p1VR5LQ2K0DdqpSKBuxdxRpP8vBYISMnpNSNZ2SzXPcaA5NxuvJaGlXXHdH00NnDL2nXa');

// Données statiques en dehors du composant pour éviter les re-créations
const TESTIMONIALS = [
  {
    text: "J'avais peur de me lancer, mais j'ai été très surprise ! On m'avait fait un devis à 4000€ ailleurs. Ici, j'ai eu un site magnifique pour beaucoup moins cher.",
    author: "Natacha",
    role: "Gérante - Le Bac Paris",
    location: "Paris",
    avatar: "NL",
    website: "https://le-bac-paris.e-application.fr"
  },
  {
    text: "Enfin une équipe qui comprend les besoins des commerçants. Support réactif, modifications rapides, je recommande à 100%.",
    author: "Joao",
    role: "Propriétaire - AJ Barber",
    location: "Paris 17ème",
    avatar: "JA",
    website: "https://ajbarber.e-application.fr"
  },
  {
    text: "Le meilleur rapport qualité-prix du marché. Mon ancien prestataire me facturait 300€/mois pour moins de services.",
    author: "Alef",
    role: "Directeur - AYANI Coiffure",
    location: "Paris",
    avatar: "AA",
    website: "https://ayani-coiffure.e-application.fr"
  },
  {
    text: "Un service professionnel et une équipe à l'écoute. Mon site est exactement comme je l'imaginais, mes clients adorent !",
    author: "Gérard",
    role: "Gérant - Panozzi",
    location: "Vincennes",
    avatar: "GP",
    website: "https://panozzi.e-application.fr"
  }
];

function AbonnementSitePwa() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [formData, setFormData] = useState({
    nom: '',
    entreprise: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [error, setError] = useState('');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [processVisible, setProcessVisible] = useState(false);
  const [pricingVisible, setPricingVisible] = useState(false);
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectUrl, setProjectUrl] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const processRef = useRef(null);
  const pricingRef = useRef(null);
  const pricingFormRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccess(true);
    }
    if (searchParams.get('canceled') === 'true') {
      setCanceled(true);
    }
  }, [searchParams]);

  // Scroll to hash on page load
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Show call popup after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCallPopup(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for Process section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setProcessVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (processRef.current) {
      observer.observe(processRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Intersection Observer for Pricing section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setPricingVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (pricingRef.current) {
      observer.observe(pricingRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Scroll vers le formulaire quand il s'affiche
  useEffect(() => {
    if (showPricingForm && pricingFormRef.current) {
      setTimeout(() => {
        pricingFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [showPricingForm]);

  // Fonction pour scroller vers le formulaire de prix
  const scrollToForm = useCallback(() => {
    setShowPricingForm(true);
    if (pricingRef.current) {
      setTimeout(() => {
        pricingRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'telephone') {
      let formatted = value.replace(/\D/g, '');
      if (formatted.length > 0) {
        formatted = formatted.match(/.{1,2}/g)?.join(' ') || formatted;
      }
      setFormData(prev => ({ ...prev, [name]: formatted.substring(0, 14) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.nom || !formData.email || !formData.telephone) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/landing/subscribe-pwa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.clientSecret) {
        setClientSecret(result.clientSecret);
        setShowPaymentModal(true);
        setLoading(false);
      } else {
        setError(result.error || 'Une erreur est survenue');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Callback pour la complétion du paiement
  const onComplete = useCallback(() => {
    setShowPaymentModal(false);
    setSuccess(true);
    setClientSecret('');
  }, []);


  return (
    <div className="lp">
      {/* Navigation */}
      <nav className="lp-nav">
        <div className="lp-nav-container">
          <div className="lp-logo" onClick={() => setShowSiteModal(true)} style={{cursor: 'pointer'}}>
            <span>E-Shops PRO</span>
          </div>
          <div className="lp-nav-links">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Accueil</a>
            <a href="#features">Avantages</a>
            <a href="#pricing">Tarifs</a>
            <a href="#testimonials">Témoignages</a>
            <a href="#faq">Questions fréquentes</a>
          </div>
          <a href="#pricing" className="lp-nav-cta">Commencer</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="lp-hero" id="hero">
        <div className="lp-hero-bg">
          <div className="lp-hero-gradient"></div>
          <div className="lp-hero-pattern"></div>
          <div className="lp-hero-glow"></div>

          {/* Animated Shapes */}
          <div className="lp-hero-shapes">
            <div className="lp-hero-shape lp-hero-shape-1"></div>
            <div className="lp-hero-shape lp-hero-shape-2"></div>
            <div className="lp-hero-shape lp-hero-shape-3"></div>
            <div className="lp-hero-shape lp-hero-shape-4"></div>
            <div className="lp-hero-shape lp-hero-shape-5"></div>
            <div className="lp-hero-shape lp-hero-shape-6"></div>
            <div className="lp-hero-shape lp-hero-shape-7"></div>
          </div>

          {/* Animated Lines */}
          <div className="lp-hero-lines">
            <div className="lp-hero-line lp-hero-line-1"></div>
            <div className="lp-hero-line lp-hero-line-2"></div>
            <div className="lp-hero-line lp-hero-line-3"></div>
          </div>

          {/* Floating Particles */}
          <div className="lp-hero-particles">
            <div className="lp-hero-particle lp-hero-particle-1"></div>
            <div className="lp-hero-particle lp-hero-particle-2"></div>
            <div className="lp-hero-particle lp-hero-particle-3"></div>
            <div className="lp-hero-particle lp-hero-particle-4"></div>
            <div className="lp-hero-particle lp-hero-particle-5"></div>
            <div className="lp-hero-particle lp-hero-particle-6"></div>
            <div className="lp-hero-particle lp-hero-particle-7"></div>
            <div className="lp-hero-particle lp-hero-particle-8"></div>
          </div>
        </div>

        <div className="lp-hero-container">
          <div className="lp-hero-content">
            <div className="lp-hero-badge">
              <span className="lp-badge-dot"></span>
              Pack PWA
            </div>

            <h1 className="lp-hero-title">
              Site Web +
              <span className="lp-hero-title-highlight"> PWA Mobile</span>
              <br />clé en main
            </h1>

            <p className="lp-hero-subtitle">
              <span className="lp-hide-mobile">Site web + Application Web Progressive installable sur mobile, maintenance incluse.<br /></span>
              Soyez présent partout, <strong>E-Shops PRO</strong> s'occupe de tout.
            </p>

            <div className="lp-hero-price">
              <div className="lp-hero-price-tag">
                <span className="lp-price-amount">189</span>
                <span className="lp-price-currency">€</span>
                <span className="lp-price-period">/mois</span>
              </div>
              <div className="lp-hero-price-info">
                <span>Site + PWA inclus</span>
                <span>•</span>
                <span>Support 7j/7</span>
              </div>
            </div>

            <div className="lp-hero-cta">
              <button onClick={scrollToForm} className="lp-btn lp-btn-primary lp-btn-lg">
                <span>Créer mon pack</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
              <a href="#features" className="lp-btn lp-btn-ghost">
                En savoir plus
              </a>
            </div>

            <div className="lp-hero-trust">
              <div className="lp-hero-avatars">
                <div className="lp-avatar">A</div>
                <div className="lp-avatar">M</div>
                <div className="lp-avatar">S</div>
                <div className="lp-avatar">+</div>
              </div>
              <div className="lp-hero-trust-text">
                <strong>98% de clients satisfaits</strong> nous recommandent à leur entourage
              </div>
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-mockup">
              <div className="lp-mockup-browser">
                <div className="lp-mockup-toolbar">
                  <div className="lp-mockup-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <div className="lp-mockup-url">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <span>votre-entreprise.fr</span>
                  </div>
                </div>
                <div className="lp-mockup-screen">
                  <div className="lp-mockup-nav"></div>
                  <div className="lp-mockup-hero-img"></div>
                  <div className="lp-mockup-text"></div>
                  <div className="lp-mockup-cards">
                    <div className="lp-mockup-card"></div>
                    <div className="lp-mockup-card"></div>
                    <div className="lp-mockup-card"></div>
                  </div>
                </div>
              </div>

              <div className="lp-mockup-phone">
                <div className="lp-phone-notch"></div>
                <div className="lp-phone-screen">
                  <div className="lp-phone-header"></div>
                  <div className="lp-phone-img"></div>
                  <div className="lp-phone-content">
                    <div className="lp-phone-line"></div>
                    <div className="lp-phone-line short"></div>
                  </div>
                </div>
              </div>

              <div className="lp-floating lp-floating-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>SEO Optimisé</span>
              </div>

              <div className="lp-floating lp-floating-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                <span>Mobile First</span>
              </div>

              <div className="lp-floating lp-floating-3">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
                <span>Ultra Rapide</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className={`lp-process ${processVisible ? 'lp-process-visible' : ''}`} ref={processRef}>
        <div className="lp-process-container">
          <div className="lp-process-header">
            <span className="lp-section-tag">Comment ça marche</span>
            <h2>Site + PWA en <span>3 étapes</span></h2>
          </div>

          <div className="lp-process-steps">
            <div className="lp-process-step" style={{'--step-delay': '0s'}}>
              <div className="lp-step-number">01</div>
              <div className="lp-step-content">
                <h3>Échange & Brief</h3>
                <p>Un expert vous appelle pour comprendre votre activité, vos besoins et définir votre site et PWA.</p>
              </div>
              <div className="lp-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
            </div>

            <div className="lp-process-line" style={{'--line-delay': '0.4s'}}></div>

            <div className="lp-process-step" style={{'--step-delay': '0.6s'}}>
              <div className="lp-step-number">02</div>
              <div className="lp-step-content">
                <h3>Création & Validation</h3>
                <p>Notre équipe crée votre site web et votre PWA. Vous validez chaque étape.</p>
              </div>
              <div className="lp-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                </svg>
              </div>
            </div>

            <div className="lp-process-line" style={{'--line-delay': '1s'}}></div>

            <div className="lp-process-step" style={{'--step-delay': '1.2s'}}>
              <div className="lp-step-number">03</div>
              <div className="lp-step-content">
                <h3>Publication</h3>
                <p>Site en ligne + PWA installable sur mobile ! On gère hébergement, mises à jour et maintenance.</p>
              </div>
              <div className="lp-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Modern Redesign */}
      <section className="lp-features-new" id="features">
        <div className="lp-features-new-bg">
          <div className="lp-features-new-gradient"></div>
          <div className="lp-features-new-grid"></div>
        </div>

        <div className="lp-features-new-container">
          {/* Header */}
          <div className="lp-features-new-header">
            <span className="lp-section-tag">Notre différence</span>
            <h2>De vrais humains, <span>pas des robots</span></h2>
            <p>À l'ère de l'IA, nous croyons que rien ne remplace le contact humain. Votre projet mérite une vraie écoute.</p>
          </div>

          {/* Human Support Hero Block */}
          <div className="lp-human-hero">
            <div className="lp-human-hero-content">
              <div className="lp-human-badge">
                <span className="lp-human-badge-dot"></span>
                Support 100% Humain
              </div>
              <h3>Un interlocuteur dédié qui <span>comprend votre métier</span></h3>
              <p>Fini les chatbots et les réponses automatiques. Chez nous, vous parlez à de vraies personnes qui prennent le temps de comprendre vos besoins, votre secteur d'activité et vos objectifs.</p>

              <div className="lp-human-stats">
                <div className="lp-human-stat">
                  <div className="lp-human-stat-number">98%</div>
                  <div className="lp-human-stat-label">Satisfaction client</div>
                </div>
                <div className="lp-human-stat">
                  <div className="lp-human-stat-number">100%</div>
                  <div className="lp-human-stat-label">Équipe française</div>
                </div>
                <div className="lp-human-stat">
                  <div className="lp-human-stat-number">6+</div>
                  <div className="lp-human-stat-label">Années d'expérience</div>
                </div>
              </div>

              <div className="lp-human-features">
                <div className="lp-human-feature">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span>Joignable par téléphone</span>
                </div>
                <div className="lp-human-feature">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>Réponses personnalisées</span>
                </div>
                <div className="lp-human-feature">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                  <span>Disponible en heures ouvrées</span>
                </div>
              </div>
            </div>

            <div className="lp-human-hero-visual">
              {/* Phone Call Animation */}
              <div className="lp-phone-animation">
                {/* Person 1 - Support Agent */}
                <div className="lp-phone-person lp-phone-person-1">
                  <div className="lp-phone-person-avatar">
                    <div className="lp-phone-person-img">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                    {/* Waves emanating from avatar */}
                    <div className="lp-avatar-wave lp-avatar-wave-1"></div>
                    <div className="lp-avatar-wave lp-avatar-wave-2"></div>
                    <div className="lp-avatar-wave lp-avatar-wave-3"></div>
                  </div>
                  <div className="lp-phone-person-info">
                    <span className="lp-phone-person-name">Marie</span>
                    <span className="lp-phone-person-role">Conseillère E-Shops</span>
                  </div>
                </div>

                {/* Person 2 - Client */}
                <div className="lp-phone-person lp-phone-person-2">
                  <div className="lp-phone-person-avatar lp-phone-person-avatar-client">
                    <div className="lp-phone-person-img">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                    {/* Waves emanating from avatar */}
                    <div className="lp-avatar-wave lp-avatar-wave-1 lp-avatar-wave-green"></div>
                    <div className="lp-avatar-wave lp-avatar-wave-2 lp-avatar-wave-green"></div>
                    <div className="lp-avatar-wave lp-avatar-wave-3 lp-avatar-wave-green"></div>
                  </div>
                  <div className="lp-phone-person-info">
                    <span className="lp-phone-person-name">Vous</span>
                    <span className="lp-phone-person-role">Notre client</span>
                  </div>
                </div>
              </div>

              {/* Floating Labels - Outside animation for better mobile layout */}
              <div className="lp-phone-badges">
                <div className="lp-phone-floating lp-phone-floating-1">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>Appel en cours</span>
                </div>
                <div className="lp-phone-floating lp-phone-floating-2">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>100% Humain</span>
                </div>
              </div>
            </div>
          </div>

          {/* Try Our Service CTA */}
          <div className="lp-try-service">
            <div className="lp-try-service-content">
              <span className="lp-try-service-question">Pas convaincu ?</span>
              <h4>Essayez notre service client</h4>
              <p>Appelez-nous maintenant et constatez par vous-même la différence</p>
              <a href="tel:+33743678501" className="lp-try-service-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>07 43 67 85 01</span>
              </a>
            </div>
          </div>

          {/* Expertise Section */}
          <div className="lp-expertise-section">
            <div className="lp-expertise-header">
              <h3>On connaît <span>votre métier</span></h3>
              <p>Plus de 6 ans d'expérience avec les artisans, commerçants et PME</p>
            </div>

            <div className="lp-expertise-grid">
              <div className="lp-expertise-card">
                <div className="lp-expertise-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <h4>Artisans du bâtiment</h4>
                <p>Plombiers, électriciens, maçons... On parle votre langue</p>
              </div>

              <div className="lp-expertise-card">
                <div className="lp-expertise-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <path d="M16 10a4 4 0 0 1-8 0"/>
                  </svg>
                </div>
                <h4>Commerçants</h4>
                <p>Boutiques, restaurants, salons... On vous accompagne</p>
              </div>

              <div className="lp-expertise-card">
                <div className="lp-expertise-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <h4>Professions libérales</h4>
                <p>Avocats, médecins, consultants... On comprend vos enjeux</p>
              </div>

              <div className="lp-expertise-card">
                <div className="lp-expertise-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </div>
                <h4>PME & TPE</h4>
                <p>De 1 à 50 salariés, on s'adapte à votre taille</p>
              </div>
            </div>
          </div>

          {/* Technical Features with Mockup */}
          <div className="lp-tech-section">
            <div className="lp-tech-visual">
              <div className="lp-tech-mockup">
                <div className="lp-tech-mockup-bar">
                  <div className="lp-tech-mockup-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <div className="lp-tech-mockup-url">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <span>votre-entreprise.fr</span>
                  </div>
                </div>
                <div className="lp-tech-mockup-content">
                  <div className="lp-tech-mockup-header"></div>
                  <div className="lp-tech-mockup-hero">
                    <div className="lp-tech-mockup-title"></div>
                    <div className="lp-tech-mockup-subtitle"></div>
                    <div className="lp-tech-mockup-btn"></div>
                  </div>
                  <div className="lp-tech-mockup-grid">
                    <div className="lp-tech-mockup-card"></div>
                    <div className="lp-tech-mockup-card"></div>
                    <div className="lp-tech-mockup-card"></div>
                  </div>
                </div>

                {/* Phone mockup */}
                <div className="lp-tech-phone">
                  <div className="lp-tech-phone-notch"></div>
                  <div className="lp-tech-phone-content">
                    <div className="lp-tech-phone-header"></div>
                    <div className="lp-tech-phone-img"></div>
                    <div className="lp-tech-phone-text"></div>
                    <div className="lp-tech-phone-text short"></div>
                  </div>
                </div>

                {/* Floating badges */}
                <div className="lp-tech-badge lp-tech-badge-2">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <span>SEO Optimisé</span>
                </div>
                <div className="lp-tech-badge lp-tech-badge-3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                  <span>100% Mobile</span>
                </div>
              </div>
            </div>

            <div className="lp-tech-content">
              <h3>Un site <span>techniquement parfait</span></h3>
              <p>Pendant qu'on s'occupe de la technique, vous vous concentrez sur vos clients</p>

              <div className="lp-tech-features">
                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Ultra-rapide</h4>
                    <p>Performance optimale garantie</p>
                  </div>
                </div>

                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Visible sur Google</h4>
                    <p>SEO optimisé pour être trouvé</p>
                  </div>
                </div>

                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Sécurisé</h4>
                    <p>HTTPS et protection anti-piratage</p>
                  </div>
                </div>

                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Toujours à jour</h4>
                    <p>Maintenance et sauvegardes incluses</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* App Features Section */}
          <div className="lp-tech-block lp-app-block">
            <div className="lp-tech-visual">
              <div className="lp-tech-phone">
                <div className="lp-tech-phone-screen">
                  <div className="lp-app-demo">
                    <div className="lp-app-demo-header">
                      <div className="lp-app-demo-notif-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                        </svg>
                      </div>
                      <span>3 nouvelles notifications</span>
                    </div>
                    <div className="lp-app-demo-card">
                      <div className="lp-app-demo-card-icon lp-app-demo-card-promo">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      </div>
                      <div className="lp-app-demo-card-text">
                        <strong>-20% sur tout le site !</strong>
                        <span>Offre flash valable 24h</span>
                      </div>
                    </div>
                    <div className="lp-app-demo-card">
                      <div className="lp-app-demo-card-icon lp-app-demo-card-loyalty">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                      </div>
                      <div className="lp-app-demo-card-text">
                        <strong>50 points fidélité gagnés</strong>
                        <span>Plus que 20 points pour -10%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lp-tech-badge lp-tech-badge-app">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18" stroke="white" strokeWidth="2"/>
                </svg>
                iOS & Android
              </div>
            </div>

            <div className="lp-tech-content">
              <h3>Une PWA <span>qui fidélise</span></h3>
              <p>Gardez le contact avec vos clients et boostez vos ventes avec des outils puissants</p>

              <div className="lp-tech-features">
                <div className="lp-tech-feature lp-tech-feature-highlight">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                      <line x1="12" y1="18" x2="12.01" y2="18"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Votre logo sur leur écran</h4>
                    <p>Le meilleur panneau publicitaire : le téléphone de vos clients</p>
                  </div>
                </div>

                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="9" cy="21" r="1"/>
                      <circle cx="20" cy="21" r="1"/>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Vente en ligne</h4>
                    <p>E-commerce intégré et paiement sécurisé</p>
                  </div>
                </div>

                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Carte de fidélité</h4>
                    <p>Points et récompenses automatiques</p>
                  </div>
                </div>

                <div className="lp-tech-feature">
                  <div className="lp-tech-feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div className="lp-tech-feature-text">
                    <h4>Espace client</h4>
                    <p>Historique et suivi des commandes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Pricing Section - Premium */}
      <section className={`lp-pricing ${pricingVisible ? 'lp-pricing-visible' : ''}`} id="pricing" ref={pricingRef}>
        <div className="lp-pricing-bg">
          <div className="lp-pricing-gradient"></div>
          <div className="lp-pricing-grid-pattern"></div>
          <div className="lp-pricing-orb lp-pricing-orb-1"></div>
          <div className="lp-pricing-orb lp-pricing-orb-2"></div>
          <div className="lp-pricing-orb lp-pricing-orb-3"></div>
        </div>

        <div className="lp-pricing-container">
          <div className="lp-pricing-header">
            <span className="lp-section-tag">Tarif unique</span>
            <h2>Un prix simple, <span>tout compris</span></h2>
            <p>Pas de frais cachés, pas de mauvaise surprise</p>
          </div>

          <div className="lp-pricing-card-wrapper">
            {!showPricingForm ? (
              <>
                <div className="lp-pricing-card">
                  <div className="lp-pricing-card-glow"></div>
                  <div className="lp-pricing-popular">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                    <span>Recommandé</span>
                  </div>

                  <div className="lp-pricing-top">
                    <div className="lp-pricing-badge-pro">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      Pack Site + PWA Mobile
                    </div>
                    <p>Votre présence web et mobile complète</p>

                    <div className="lp-pricing-price">
                      <span className="lp-pricing-amount">189</span>
                      <span className="lp-pricing-currency">€</span>
                      <span className="lp-pricing-period">/mois</span>
                    </div>
                    <div className="lp-pricing-billing">Facturé mensuellement</div>
                  </div>

                  <div className="lp-pricing-divider">
                    <span>Inclus dans votre offre</span>
                  </div>

                  <div className="lp-pricing-features">
                    {[
                      'Site web professionnel sur mesure',
                      'PWA installable sur iOS & Android',
                      'Icône sur l\'écran d\'accueil',
                      'Hébergement premium inclus',
                      'Certificat SSL (https)',
                      'Maintenance & mises à jour',
                      'Support téléphonique français 7j/7',
                      'Statistiques de visite',
                      'Modifications incluses à vie'
                    ].map((feature, index) => (
                      <div className="lp-pricing-feature" key={index} style={{'--feature-delay': `${index * 0.08}s`}}>
                        <div className="lp-pricing-feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button onClick={() => setShowPricingForm(true)} className="lp-pricing-cta">
                    <span className="lp-pricing-cta-bg"></span>
                    <span className="lp-pricing-cta-text">Commencer maintenant</span>
                    <span className="lp-pricing-cta-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </span>
                  </button>

                </div>

                {/* Floating elements */}
                <div className="lp-pricing-float lp-pricing-float-1">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>Sans surprise</span>
                </div>
                <div className="lp-pricing-float lp-pricing-float-2">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <span>Paiement sécurisé</span>
                </div>
                <div className="lp-pricing-float lp-pricing-float-3">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                  <span>Tout inclus</span>
                </div>
              </>
            ) : (
              <div className="lp-pricing-form-card" ref={pricingFormRef}>
                <button className="lp-pricing-back" onClick={() => setShowPricingForm(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  <span>Retour</span>
                </button>

                <div className="lp-pricing-form-header">
                  <h3>Finalisez votre commande</h3>
                  <div className="lp-pricing-form-price">
                    <span className="lp-pricing-form-amount">189€</span>
                    <span className="lp-pricing-form-period">/mois</span>
                  </div>
                </div>

                {canceled && (
                  <div className="lp-message lp-message-canceled">
                    <div className="lp-message-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                    </div>
                    <h3>Paiement annulé</h3>
                    <p>Pas de souci, vous pouvez réessayer quand vous voulez.</p>
                    <button className="lp-btn lp-btn-primary" onClick={() => setCanceled(false)}>
                      Réessayer
                    </button>
                  </div>
                )}

                {success && (
                  <div className="lp-message lp-message-success">
                    <div className="lp-message-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    </div>
                    <h3>Félicitations !</h3>
                    <p>Votre abonnement est actif. Notre équipe vous contacte sous 24h.</p>
                  </div>
                )}

                {!success && !canceled && (
                  <form className="lp-pricing-form" onSubmit={handleSubmit}>
                    {error && <div className="lp-form-error">{error}</div>}

                    <div className="lp-form-grid">
                      <div className="lp-form-group">
                        <label htmlFor="nom">Nom complet *</label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          placeholder="Jean Dupont"
                          required
                        />
                      </div>

                      <div className="lp-form-group">
                        <label htmlFor="entreprise">Entreprise</label>
                        <input
                          type="text"
                          id="entreprise"
                          name="entreprise"
                          value={formData.entreprise}
                          onChange={handleChange}
                          placeholder="Ma Super Entreprise"
                        />
                      </div>

                      <div className="lp-form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jean@exemple.fr"
                          required
                        />
                      </div>

                      <div className="lp-form-group">
                        <label htmlFor="telephone">Téléphone *</label>
                        <input
                          type="tel"
                          id="telephone"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          placeholder="06 12 34 56 78"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      className={`lp-btn-premium ${loading ? 'loading' : ''}`}
                      disabled={loading}
                    >
                      <span className="lp-btn-premium-text">Payer maintenant</span>
                      <span className="lp-btn-premium-arrow">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </span>
                    </button>

                    <div className="lp-form-footer">
                      <div className="lp-form-secure-badges">
                        <div className="lp-secure-badge">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                          <span>SSL Sécurisé</span>
                        </div>
                        <div className="lp-secure-badge">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                          </svg>
                          <span>Stripe</span>
                        </div>
                      </div>
                      <p className="lp-form-terms">Engagement 36 mois</p>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Fusionnée */}
      <section className="lp-testimonials" id="testimonials">
        <div className="lp-testimonials-container">
          <div className="lp-testimonials-header">
            <span className="lp-section-tag">Nos clients</span>
            <h2>Ils nous font <span>confiance</span></h2>
            <p>Des professionnels de tous secteurs nous recommandent</p>
          </div>

          {/* Badges métiers */}
          <div className="lp-testimonials-badges">
            <span className="lp-badge-item">Restaurants</span>
            <span className="lp-badge-item">Salons</span>
            <span className="lp-badge-item">Instituts</span>
            <span className="lp-badge-item">Commerces</span>
            <span className="lp-badge-item">Artisans</span>
          </div>

          <div className="lp-testimonials-slider">
            {TESTIMONIALS.map((testimonial, index) => (
              <div
                key={index}
                className={`lp-testimonial-card ${index === activeTestimonial ? 'active' : ''}`}
              >
                {/* Nom et rôle en premier */}
                <div className="lp-testimonial-header">
                  <div className="lp-testimonial-avatar">{testimonial.avatar}</div>
                  <div className="lp-testimonial-info">
                    <strong>{testimonial.author}</strong>
                    <span>{testimonial.role}</span>
                    <span className="lp-testimonial-location">{testimonial.location}</span>
                  </div>
                </div>

                {/* Étoiles */}
                <div className="lp-testimonial-stars">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  ))}
                </div>

                {/* Avis */}
                <blockquote className="lp-testimonial-text">
                  "{testimonial.text}"
                </blockquote>

                {/* Bouton voir projet */}
                {testimonial.website && (
                  <button
                    className="lp-testimonial-project-btn"
                    onClick={() => {
                      setProjectUrl(testimonial.website);
                      setShowProjectModal(true);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    <span>Voir leur projet</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="lp-testimonials-dots">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                className={`lp-dot ${index === activeTestimonial ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section - Sur-mesure vs Services en ligne */}
      <section className="lp-compare">
        <div className="lp-compare-bg">
          <div className="lp-compare-gradient"></div>
          <div className="lp-compare-particles">
            <div className="lp-particle lp-particle-1"></div>
            <div className="lp-particle lp-particle-2"></div>
            <div className="lp-particle lp-particle-3"></div>
          </div>
        </div>

        <div className="lp-compare-container">
          <div className="lp-compare-header">
            <span className="lp-section-tag lp-tag-green">Comparatif</span>
            <h2>Pourquoi choisir <span>E-Shops PRO</span> ?</h2>
            <p>Découvrez ce qui nous différencie des plateformes classiques</p>
          </div>

          {/* Comparaison par plateforme */}
          <div className="lp-compare-platforms">

            {/* Section Concurrents */}
            <div className="lp-competitors-section">
              <div className="lp-competitors-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>Les agences classiques</span>
                <small>Développement de PWA</small>
              </div>

              <div className="lp-competitors-cards">
                <div className="lp-platform-card lp-platform-competitor lp-platform-wordpress" style={{'--delay': '0.1s'}}>
                  <div className="lp-competitor-badge">Concurrent</div>
                  <div className="lp-platform-header">
                    <span className="lp-platform-name">GoodBarber + Wix</span>
                    <span className="lp-platform-price">348€<small>/mois</small></span>
                  </div>
                  <ul className="lp-platform-features">
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>App 192€ + Site 45€ + Options</span>
                    </li>
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Vous faites tout vous-même</span>
                    </li>
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Push notifications en supplément</span>
                    </li>
                  </ul>
                </div>

                <div className="lp-platform-card lp-platform-competitor lp-platform-webflow" style={{'--delay': '0.2s'}}>
                  <div className="lp-competitor-badge">Concurrent</div>
                  <div className="lp-platform-header">
                    <span className="lp-platform-name">BuildFire + Squarespace</span>
                    <span className="lp-platform-price">499€<small>/mois</small></span>
                  </div>
                  <ul className="lp-platform-features">
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>App Pro 449€ + Site 50€</span>
                    </li>
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Plugins payants obligatoires</span>
                    </li>
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Support en anglais uniquement</span>
                    </li>
                  </ul>
                </div>

                <div className="lp-platform-card lp-platform-competitor lp-platform-shopify" style={{'--delay': '0.3s'}}>
                  <div className="lp-competitor-badge">Concurrent</div>
                  <div className="lp-platform-header">
                    <span className="lp-platform-name">Agence classique</span>
                    <span className="lp-platform-price">800€<small>/mois</small></span>
                  </div>
                  <ul className="lp-platform-features">
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>+ 15 000€ de création initiale</span>
                    </li>
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Modifications facturées en +</span>
                    </li>
                    <li className="lp-platform-bad">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      <span>Délai 3-6 mois minimum</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* VS Separator */}
            <div className="lp-vs-separator">
              <div className="lp-vs-line"></div>
              <div className="lp-vs-badge">VS</div>
              <div className="lp-vs-line"></div>
            </div>

            {/* Section Notre Solution */}
            <div className="lp-solution-section">
              <div className="lp-solution-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>Notre solution</span>
                <small>Site + App Mobile tout inclus</small>
              </div>

              <div className="lp-platform-card lp-platform-us" style={{'--delay': '0.6s'}}>
                <div className="lp-platform-badge">
                  <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  Recommandé
                </div>
                <div className="lp-platform-header">
                  <span className="lp-platform-name">E-Shops PRO</span>
                  <span className="lp-platform-price lp-platform-price-us">189€<small>/mois</small></span>
                </div>
                <ul className="lp-platform-features">
                  <li className="lp-platform-good">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong>Site + App</strong> inclus</span>
                  </li>
                  <li className="lp-platform-good">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong>iOS + Android</strong></span>
                  </li>
                  <li className="lp-platform-good">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong>Notifications push</strong></span>
                  </li>
                  <li className="lp-platform-good">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong>Support 7j/7</strong></span>
                  </li>
                </ul>
                <a href="#pricing" className="lp-platform-cta">
                  Choisir cette offre
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>

          <div className="lp-compare-cta">
            <a href="#pricing" className="lp-btn lp-btn-primary lp-btn-lg lp-btn-glow">
              <span>Je choisis le sur-mesure</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section - Chat Mockup */}
      <section className="lp-faq" id="faq">
        <div className="lp-faq-container">
          <div className="lp-faq-header">
            <span className="lp-section-tag">FAQ</span>
            <h2>Questions <span>fréquentes</span></h2>
            <p>Discutez avec notre équipe pour toutes vos questions</p>
          </div>

          <div className="lp-faq-chat">
            {/* Chat Window - Clickable to WhatsApp */}
            <a href="https://wa.me/33743678501?text=Bonjour%2C%20j%27ai%20une%20question%20concernant%20vos%20services" target="_blank" rel="noopener noreferrer" className="lp-chat-window-link">
            <div className="lp-chat-window">
              <div className="lp-chat-header">
                <div className="lp-chat-header-avatar">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
                <div className="lp-chat-header-info">
                  <span className="lp-chat-header-name">Support E-Shop</span>
                  <span className="lp-chat-header-status">
                    <span className="lp-chat-status-dot"></span>
                    En ligne
                  </span>
                </div>
                <div className="lp-chat-header-actions">
                  <span className="lp-chat-dot"></span>
                  <span className="lp-chat-dot"></span>
                  <span className="lp-chat-dot"></span>
                </div>
              </div>

              <div className="lp-chat-messages">
                <div className="lp-chat-scroll">
                  {/* Message 1 */}
                  <div className="lp-chat-message lp-chat-user">
                    <div className="lp-chat-bubble">
                      Combien de temps pour avoir mon site et ma PWA ?
                    </div>
                  </div>
                  <div className="lp-chat-message lp-chat-support">
                    <div className="lp-chat-avatar">M</div>
                    <div className="lp-chat-bubble">
                      Le site et la PWA sont prêts en 2-3 semaines après notre premier échange. C'est plus rapide qu'une app native !
                    </div>
                  </div>

                  {/* Message 2 */}
                  <div className="lp-chat-message lp-chat-user">
                    <div className="lp-chat-bubble">
                      C'est quoi exactement une PWA ?
                    </div>
                  </div>
                  <div className="lp-chat-message lp-chat-support">
                    <div className="lp-chat-avatar">M</div>
                    <div className="lp-chat-bubble">
                      Une Progressive Web App s'installe comme une app native sur le téléphone de vos clients, avec votre icône sur leur écran d'accueil !
                    </div>
                  </div>

                  {/* Message 3 */}
                  <div className="lp-chat-message lp-chat-user">
                    <div className="lp-chat-bubble">
                      Pas besoin de passer par l'App Store ?
                    </div>
                  </div>
                  <div className="lp-chat-message lp-chat-support">
                    <div className="lp-chat-avatar">M</div>
                    <div className="lp-chat-bubble">
                      Non ! La PWA s'installe directement depuis votre site. Pas de commission Apple/Google, pas de validation à attendre. C'est instantané !
                    </div>
                  </div>

                  {/* Message 4 */}
                  <div className="lp-chat-message lp-chat-user">
                    <div className="lp-chat-bubble">
                      La carte de fidélité est vraiment incluse ?
                    </div>
                  </div>
                  <div className="lp-chat-message lp-chat-support">
                    <div className="lp-chat-avatar">M</div>
                    <div className="lp-chat-bubble">
                      Oui ! Points automatiques, récompenses personnalisables, historique client... Tout est inclus, pas de frais cachés.
                    </div>
                  </div>

                  {/* Message 5 */}
                  <div className="lp-chat-message lp-chat-user">
                    <div className="lp-chat-bubble">
                      Je peux vendre en ligne via la PWA ?
                    </div>
                  </div>
                  <div className="lp-chat-message lp-chat-support">
                    <div className="lp-chat-avatar">M</div>
                    <div className="lp-chat-bubble">
                      Absolument ! E-commerce intégré avec paiement sécurisé (CB, Apple Pay, Google Pay). Vos clients commandent en 2 clics. 🛒
                    </div>
                  </div>

                  {/* Duplicate for seamless loop */}
                  <div className="lp-chat-message lp-chat-user">
                    <div className="lp-chat-bubble">
                      Combien de temps pour avoir mon site et ma PWA ?
                    </div>
                  </div>
                  <div className="lp-chat-message lp-chat-support">
                    <div className="lp-chat-avatar">M</div>
                    <div className="lp-chat-bubble">
                      Le site et la PWA sont prêts en 2-3 semaines après notre premier échange. C'est plus rapide qu'une app native !
                    </div>
                  </div>
                </div>
              </div>

              <div className="lp-chat-input">
                <span className="lp-chat-input-text">Cliquez pour discuter sur WhatsApp</span>
                <span className="lp-chat-whatsapp-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
              </div>
            </div>
            </a>

            {/* Side Info */}
            <div className="lp-faq-side">
              <div className="lp-faq-side-card">
                <div className="lp-faq-side-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <h4>Appelez-nous</h4>
                <p>Notre équipe est disponible pour répondre à toutes vos questions</p>
                <a href="tel:+33743678501" className="lp-faq-side-link">07 43 67 85 01</a>
              </div>

              <div className="lp-faq-side-card">
                <div className="lp-faq-side-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <h4>Réponse rapide</h4>
                <p>Nous répondons en moyenne en moins de 2 heures</p>
                <span className="lp-faq-side-badge">Support réactif</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-container">
          <div className="lp-footer-brand">
            <div className="lp-logo">
              <span>E-Shops PRO</span>
            </div>
            <p>Votre partenaire digital pour réussir en ligne</p>
          </div>

          <div className="lp-footer-links">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Accueil</a>
            <a href="#features">Avantages</a>
            <a href="#pricing">Tarifs</a>
            <a href="#testimonials">Témoignages</a>
            <a href="#faq">Questions fréquentes</a>
          </div>

          <div className="lp-footer-bottom">
            <p>© {new Date().getFullYear()} E-Shops PRO. Tous droits réservés.</p>
            <p className="lp-footer-legal-line">
              E-SHOPS PRO SAS · SIRET 881 252 795 00011 · TVA FR20 881 252 795 · 12 rue de la Roquette, 75011 Paris
            </p>
          </div>
        </div>
      </footer>

      {/* Modal Site E-Shops PRO */}
      {showSiteModal && (
        <div className="lp-site-modal">
          <div className="lp-site-modal-header">
            <span>E-Shops PRO</span>
            <button className="lp-site-modal-close" onClick={() => setShowSiteModal(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>Retour</span>
            </button>
          </div>
          <iframe
            src="https://e-shop-pro.fr"
            title="E-Shops PRO"
            className="lp-site-modal-iframe"
          />
        </div>
      )}

      {/* Popup Call CTA */}
      {showCallPopup && (
        <div className="lp-call-popup">
          <div className="lp-call-popup-header">
            <h5 className="lp-call-popup-title">Site + PWA clé en main</h5>
            <button className="lp-call-popup-close" onClick={() => setShowCallPopup(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="lp-call-popup-content">
            {/* Mockup Site Web + Mini Phone */}
            <div className="lp-popup-mockup">
              <div className="lp-popup-browser">
                <div className="lp-popup-browser-bar">
                  <div className="lp-popup-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <div className="lp-popup-url">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <span>votre-site.fr</span>
                  </div>
                </div>
                <div className="lp-popup-screen">
                  <div className="lp-popup-nav">
                    <div className="lp-popup-logo"></div>
                    <div className="lp-popup-menu">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                  <div className="lp-popup-hero">
                    <div className="lp-popup-hero-text">
                      <div className="lp-popup-title"></div>
                      <div className="lp-popup-subtitle"></div>
                      <div className="lp-popup-btn"></div>
                    </div>
                    <div className="lp-popup-hero-img"></div>
                  </div>
                  <div className="lp-popup-cards">
                    <div className="lp-popup-card">
                      <div className="lp-popup-card-icon"></div>
                      <div className="lp-popup-card-title"></div>
                      <div className="lp-popup-card-text"></div>
                    </div>
                    <div className="lp-popup-card">
                      <div className="lp-popup-card-icon"></div>
                      <div className="lp-popup-card-title"></div>
                      <div className="lp-popup-card-text"></div>
                    </div>
                    <div className="lp-popup-card">
                      <div className="lp-popup-card-icon"></div>
                      <div className="lp-popup-card-title"></div>
                      <div className="lp-popup-card-text"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Mini Phone */}
              <div className="lp-popup-mini-phone">
                <div className="lp-popup-mini-phone-notch"></div>
                <div className="lp-popup-mini-phone-screen">
                  <div className="lp-popup-mini-phone-notif"></div>
                  <div className="lp-popup-mini-phone-content"></div>
                  <div className="lp-popup-mini-phone-btn"></div>
                </div>
              </div>
              <div className="lp-popup-mockup-label">Site + PWA installable mobile</div>
            </div>

            {/* CTA */}
            <div className="lp-call-popup-cta">
              <span className="lp-call-popup-question">Des questions ?</span>
              <h4>Parlons de votre projet</h4>
              <p>Appelez-nous pour discuter de votre site et PWA</p>
              <a href="tel:+33743678501" className="lp-call-popup-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>07 43 67 85 01</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Modal Projet Client */}
      {showProjectModal && (
        <div className="lp-project-modal">
          <div className="lp-project-modal-header">
            <span>Projet réalisé</span>
            <button className="lp-project-modal-close" onClick={() => setShowProjectModal(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>Retour à l'offre</span>
            </button>
          </div>
          <iframe
            src={projectUrl}
            title="Projet client"
            className="lp-project-modal-iframe"
          />
        </div>
      )}

      {/* Overlay de chargement plein écran */}
      {loading && (
        <div className="lp-fullscreen-loading">
          <div className="lp-fullscreen-loading-content">
            <div className="lp-fullscreen-spinner">
              <svg viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4"/>
                <circle cx="25" cy="25" r="20" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeDasharray="80, 200"/>
              </svg>
            </div>
            <div className="lp-fullscreen-text">Ouverture du paiement sécurisé</div>
            <div className="lp-fullscreen-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      )}

      {/* Modal Paiement Stripe Embedded - Premium */}
      {showPaymentModal && clientSecret && (
        <div className="lp-payment-modal">
          <div className="lp-payment-modal-overlay"></div>
          <div className="lp-payment-modal-container">
            {/* Header Premium */}
            <div className="lp-payment-modal-header">
              <div className="lp-payment-header-top">
                <div className="lp-payment-brand">
                  <span className="lp-payment-brand-name">E-Shops PRO</span>
                </div>
                <button className="lp-payment-modal-close" onClick={() => { setShowPaymentModal(false); setClientSecret(''); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="lp-payment-header-content">
                <div className="lp-payment-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="5" width="20" height="14" rx="2"/>
                    <path d="M2 10h20"/>
                  </svg>
                </div>
                <div className="lp-payment-header-text">
                  <span className="lp-payment-step">Dernière étape</span>
                  <h3>Finalisez votre commande</h3>
                </div>
              </div>
            </div>

            {/* Récapitulatif */}
            <div className="lp-payment-summary">
              <div className="lp-payment-summary-row">
                <div className="lp-payment-product">
                  <div className="lp-payment-product-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <line x1="3" y1="9" x2="21" y2="9"/>
                      <line x1="9" y1="21" x2="9" y2="9"/>
                    </svg>
                  </div>
                  <div className="lp-payment-product-info">
                    <strong>Site Web + PWA Mobile</strong>
                    <span>Site + App iOS/Android • Notifications Push • Support illimité</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulaire Stripe */}
            <div className="lp-payment-modal-checkout">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ clientSecret, onComplete }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>

            {/* Footer avec badges de confiance */}
            <div className="lp-payment-modal-footer">
              <div className="lp-payment-trust-badges">
                <div className="lp-payment-trust-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <span>SSL 256-bit</span>
                </div>
                <div className="lp-payment-trust-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <span>3D Secure</span>
                </div>
                <div className="lp-payment-trust-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>Certifié PCI</span>
                </div>
              </div>
              <div className="lp-payment-powered">
                <span>Paiement sécurisé par</span>
                <svg className="lp-stripe-logo" viewBox="0 0 60 25" fill="#635bff">
                  <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.48zm-3.67-3.07c0-1.45-.58-2.55-2.03-2.55-1.43 0-2.14 1.12-2.3 2.55h4.33zM42.52 20.7V5.58l4.14-.88v15.78c0 .88.4 1.16.93 1.16.26 0 .47-.02.78-.1v3.26c-.5.18-1.18.3-1.93.3-2.5 0-3.92-1.14-3.92-4.4zM31.36 8.91l.26-3.23h4.12v14.14h-4.12V9.88c-.86-.64-2.05-.96-3.24-.96-1.45 0-2.25.52-2.25 1.44 0 2.11 6.7 1.21 6.7 6.51 0 3.22-2.53 5.13-6.17 5.13-1.74 0-3.69-.4-4.95-1.08v-3.58c1.36.78 3.22 1.3 4.75 1.3 1.55 0 2.39-.5 2.39-1.48 0-2.25-6.68-1.26-6.68-6.41 0-3.04 2.39-5.05 5.97-5.05 1.6 0 3.09.34 4.22.96l-1 2.25zM6.13 5.06c1.5 0 2.7.34 3.6.95l-.72 2.88c-.74-.48-1.7-.81-2.8-.81-1.82 0-2.78.7-2.78 1.7 0 2.85 7.07 1.82 7.07 7.14 0 3.58-2.75 5.08-6.03 5.08-1.85 0-3.87-.48-5.03-1.24l.72-2.96c1.26.74 2.75 1.18 4.23 1.18 1.7 0 2.7-.6 2.7-1.68 0-2.97-7.07-1.94-7.07-7.08 0-3.26 2.49-5.16 5.91-5.16z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AbonnementSitePwa;
